import moment from "moment";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useJwtAsState } from "../Hooks/useJwtAsState";
import { safeGet, safeSet, safeClear } from "../utils/safeStorage";
import { useSessionCountdown } from "../Hooks/useSessionCountdown";
import { markIdleLogout, clearPriorSessionNote, notePriorSession } from "../config/sessionTimeout";
import { SessionCountdownContext } from "./SessionCountdownContext";
import { throttle } from "../util/throttle";
import onlineStatusManager from "../utils/onlineStatusManager";
import { markTeardown } from "../utils/teardownState";
import { unregisterWebPushOnLogout } from "../api/webPushService";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser] = useJwtAsState("user", null);

  useEffect(() => {
    const resetAuthTime = () => {
      // #591: safe accessors — a raw read here threw on Safari privacy modes.
      if (safeGet("token")) {
        safeSet("auth_time", moment().unix().toString());
      }
    };
    const throttledResetAuthTime = throttle(resetAuthTime, 10000);
    const activityEvents = [
      "click",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart",
    ];
    activityEvents.forEach((event) =>
      window.addEventListener(event, throttledResetAuthTime)
    );
    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, throttledResetAuthTime)
      );
    };
  }, []);


  // Assigned during render, below, from the countdown hook. A ref rather than a
  // direct call because logout is defined above the hook - and it must be
  // callable synchronously from the sign-out click, before that click bubbles
  // to the countdown's own window listener.
  const endSessionRef = useRef(null);

  const logout = async () => {
    // GMA-1370: logout must never hang on the network. Previously this
    // awaited the presence "offline" ping before clearing the session, so a
    // slow/unreachable backend froze logout until the network responded.
    // Fire the ping best-effort (no await; the server heartbeat times the
    // user out anyway) and end the session immediately + synchronously.
    try {
      Promise.resolve(onlineStatusManager.setUserOffline()).catch(() => {});
    } catch (_ignored) {
      // presence cleanup must never block logout
    }
    try {
      Promise.resolve(unregisterWebPushOnLogout()).catch(() => {});
    } catch (_ignored) {
      // web push cleanup must never block logout
    }
    // Started, NOT awaited, before the clear. Two invariants meet here and
    // both must hold:
    //
    //   GMA-1370 - logout must never hang on the network. A slow backend once
    //   froze it, so the session is cleared synchronously, before any await.
    //   GMA-1909 - the surfaces tear down over the network (the call page
    //   POSTs call/end), and a redirect fired during that request aborts it,
    //   leaving the call alive on the server.
    //
    // Both hold because the listeners run SYNCHRONOUSLY here and each CAPTURES
    // the credential it needs before returning. They do NOT simply "construct
    // their requests before the clear" - an earlier version of this comment
    // said that and it was wrong: apiRequest awaits getValidAccessToken(),
    // which reads storage, and that await lands AFTER the clear below. A
    // teardown listener therefore passes an explicit accessToken and sets
    // signingOut, so nothing goes back to storage and no rotated token is
    // written back into a session that just ended.
    //
    // What the returned promise buys is the NAVIGATION delay, which is the
    // caller's business, not this function's.
    // Issue #637 deliberately marks NO teardown here. `logout()` is not a
    // proxy for "the document is going away": five of its six call sites never
    // navigate (Layout.jsx's sign-out button, TermsPage twice,
    // RegisterUserPage twice), and after a manual sign-out the route change is
    // React Router's `<Navigate replace>` in RequireLogin — no `pagehide`, no
    // aborted fetches. A mark here suppressed genuine outages on a live page,
    // and it also covered the session-end teardown's own `call/end` request,
    // whose failure can leave a call alive on the server. The mark lives at
    // each session-end NAVIGATION instead; `sessionEndNavigation.test.js`
    // enumerates all three and pins the adjacency.

    // Issue #672: a DELIBERATE sign-out needs no explanation, so drop the
    // "this page load had a session" note before RequireLogin redirects — it
    // is what would otherwise make the guard mark `session_ended` and greet the
    // user with a notice about a sign-out they just performed themselves. It
    // does NOT un-write a reason already marked: the idle path marks "idle"
    // and then calls this, and that copy must survive.
    clearPriorSessionNote();
    const teardown = endSessionRef.current?.();
    // #591: /register calls logout() unconditionally on mount; a raw
    // localStorage.clear() here THREW under Safari Block-All-Cookies /
    // Lockdown / teardown states (the GMA-1275 class), collapsing the tree
    // to a blank page mid-registration. safeClear degrades + breadcrumbs.
    safeClear();
    setIsAuthenticated(false);
    setIsLoggedIn(false);
    return teardown;
  };

  // GMA-1909: ONE deadline for the whole app. This replaced a second, invisible
  // 30-minute `auth_time` poll that lived here with a different event set - two
  // clocks meant the silent one could sign the user out while the visible one
  // still showed time left. `auth_time` is still stamped above because
  // useJwtAsState reads it; only the duplicate expiry poll is gone.
  const handleIdleLogout = useCallback(async () => {
    markIdleLogout();
    // Awaited HERE rather than inside logout(): the session is already cleared
    // by then (GMA-1370), and what this wait protects is the teardown request
    // that the redirect below would otherwise abort. Bounded inside
    // endSession, so a hung surface cannot hold the sign-out open.
    await logout();
    // Matches the behaviour the legacy poll had: leave the authenticated tree
    // outright rather than depending on each route to notice.
    //
    // REPLACE, never assign href: an assignment pushes, leaving the previous
    // URL in session history. /call is deliberately outside RequireLogin
    // (AppRoutes.jsx), so a pushed entry puts the call surface - and the client
    // data on it - one Back press away from a signed-out user.
    //
    // Issue #637 — announce the teardown HERE, immediately before the
    // navigation that will kill whatever is still in flight, and AFTER the
    // `await logout()` above so the teardown's own `call/end` failure stays
    // reportable. `pagehide` is only a backstop: it fires as the document
    // unloads, which is not ordered against the delivery of the fetch rejection
    // it is meant to explain.
    markTeardown();
    window.location.replace("/login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    remainingMs: sessionRemainingMs,
    isWarning: sessionIsWarning,
    resetDeadline: resetSessionDeadline,
    endSession: endSession,
  } = useSessionCountdown({
    // Both, not just currentUser: useJwtAsState does not re-derive from a
    // same-tab localStorage.clear(), so after a manual logout currentUser stays
    // populated. Gating on it alone left the countdown running on /login, able
    // to raise an idle-logout notice for an already signed-out user.
    enabled: Boolean(currentUser) && isAuthenticated,
    onExpire: handleIdleLogout,
  });

  endSessionRef.current = endSession;

  useEffect(() => {
    if (safeGet("token") !== null) setIsAuthenticated(true);
    else setIsAuthenticated(false);

    if (currentUser?.isTermsAccepted) {
      setIsLoggedIn(true);
      // #701: /call is outside RequireLogin, so that guard never notes the
      // session. Without this, a /call-only tab that loses its token has no
      // prior-session note and the missing-token path cannot mark+redirect.
      notePriorSession();
    } else setIsLoggedIn(false);

    setIsLoading(false);
  }, [currentUser]);

  // The two values that change every second are published on their OWN context.
  // AuthContext is read by large pages, so putting the tick there re-rendered
  // the entire app once a second while the user was idle - caused by the very
  // feature whose job is to be quiet until the last five minutes.
  const countdownValue = useMemo(
    () => ({ remainingMs: sessionRemainingMs, isWarning: sessionIsWarning }),
    [sessionRemainingMs, sessionIsWarning]
  );

  // Memoised so a countdown tick (which re-renders this provider) does not
  // hand consumers a new object and re-render them anyway.
  const authValue = useMemo(
    () => ({
      currentUser,
      isAuthenticated,
      isLoggedIn,
      isLoading,
      logout,
      resetSessionDeadline,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser, isAuthenticated, isLoggedIn, isLoading, resetSessionDeadline]
  );

  return (
    <AuthContext.Provider value={authValue}>
      <SessionCountdownContext.Provider value={countdownValue}>
        {children}
      </SessionCountdownContext.Provider>
    </AuthContext.Provider>
  );
};
